import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Trophy } from 'lucide-react';
import { getCompetition } from '../data/competitions';
import { lineArenas } from '../data/arenas';
import { loadRobot, loadRulePack } from '../hooks';
import { loadJSON, saveJSON, storageKeys } from '../storage';
import type { RobotValues, RulePack, SimResult } from '../types';
import OverviewTab from './tabs/OverviewTab';
import RulesTab from './tabs/RulesTab';
import ArenaTab from './tabs/ArenaTab';
import PartsTab from './tabs/PartsTab';
import BuildTab from './tabs/BuildTab';
import WireCodeTab from './tabs/WireCodeTab';
import SimulateTab from './tabs/SimulateTab';
import ImproveTab from './tabs/ImproveTab';

const TABS = ['Overview', 'Rules', 'Arena', 'Parts', 'Build', 'Wire & Code', 'Simulate', 'Improve'] as const;
type Tab = (typeof TABS)[number];

interface Props {
  id: string;
}

export default function CompetitionPage({ id }: Props) {
  const comp = getCompetition(id);
  const [tab, setTab] = useState<Tab>('Overview');

  // These hooks must run unconditionally; fall back to empty defaults if comp is missing.
  const [robot, setRobotState] = useState<RobotValues>(() => (comp ? loadRobot(comp) : {}));
  const [rulePack, setRulePackState] = useState<RulePack>(() => (comp ? loadRulePack(comp) : {}));
  const [result, setResult] = useState<SimResult | null>(() =>
    comp ? loadJSON<SimResult | null>(storageKeys.lastResult(comp.id), null) : null,
  );
  const [arenaId, setArenaIdState] = useState<string>(() =>
    comp ? loadJSON<string>(storageKeys.arena(comp.id), lineArenas[0].id) : lineArenas[0].id,
  );

  useEffect(() => {
    setTab('Overview');
    if (comp) {
      setRobotState(loadRobot(comp));
      setRulePackState(loadRulePack(comp));
      setResult(loadJSON<SimResult | null>(storageKeys.lastResult(comp.id), null));
      setArenaIdState(loadJSON<string>(storageKeys.arena(comp.id), lineArenas[0].id));
    }
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!comp) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="text-2xl font-extrabold text-white">Competition not found</h1>
        <a href="#/compete" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-amber-300">
          <ArrowLeft className="size-4" /> Back to Taa Arena
        </a>
      </div>
    );
  }

  const setRobot = (v: RobotValues) => {
    setRobotState(v);
    saveJSON(storageKeys.robot(comp.id), v);
  };

  const setRulePack = (v: RulePack) => {
    setRulePackState(v);
    saveJSON(storageKeys.rulePack(comp.id), v);
  };

  const setArenaId = (v: string) => {
    setArenaIdState(v);
    saveJSON(storageKeys.arena(comp.id), v);
  };

  const handleResult = (r: SimResult) => {
    setResult(r);
    saveJSON(storageKeys.lastResult(comp.id), r);
    const best = loadJSON<number | null>(storageKeys.bestScore(comp.id), null);
    if (best === null || r.score > best) saveJSON(storageKeys.bestScore(comp.id), r.score);
    setTab('Improve');
  };

  const best = loadJSON<number | null>(storageKeys.bestScore(comp.id), null);

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24">
      {/* Header */}
      <div className="pt-8">
        <a
          href="#/compete"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 transition hover:text-white"
        >
          <ArrowLeft className="size-4 rtl:rotate-180" />
          Taa Arena
        </a>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-3 flex flex-wrap items-start justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-black tracking-tight text-white md:text-4xl">{comp.name}</h1>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-400">{comp.tagline}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide ring-1 ${comp.chip}`}>
              {comp.difficulty}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-300">
              <Clock className="size-3.5" />
              {comp.buildTime}
            </span>
            {best !== null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/10 px-3 py-1.5 text-xs font-bold text-amber-300 ring-1 ring-amber-400/30">
                <Trophy className="size-3.5" />
                Best: {best} pts
              </span>
            )}
          </div>
        </motion.div>
      </div>

      {/* Tab bar */}
      <div className="mt-6 flex gap-1 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.03] p-1.5">
        {TABS.filter((tName) => tName !== 'Arena' || comp.id === 'line').map((tName) => (
          <button
            key={tName}
            type="button"
            onClick={() => setTab(tName)}
            className={`flex-1 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition ${
              tab === tName ? comp.button : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
            }`}
          >
            {tName}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <motion.div
        key={tab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-6"
      >
        {tab === 'Overview' && <OverviewTab comp={comp} onGoTo={(t) => setTab(t as Tab)} />}
        {tab === 'Rules' && <RulesTab comp={comp} rulePack={rulePack} setRulePack={setRulePack} />}
        {tab === 'Arena' && comp.id === 'line' && (
          <ArenaTab comp={comp} arenaId={arenaId} setArenaId={setArenaId} />
        )}
        {tab === 'Parts' && <PartsTab comp={comp} />}
        {tab === 'Build' && <BuildTab comp={comp} robot={robot} setRobot={setRobot} />}
        {tab === 'Wire & Code' && <WireCodeTab comp={comp} />}
        {tab === 'Simulate' && (
          <SimulateTab comp={comp} robot={robot} rulePack={rulePack} arenaId={arenaId} onResult={handleResult} />
        )}
        {tab === 'Improve' && (
          <ImproveTab comp={comp} robot={robot} result={result} onGoTo={(t) => setTab(t as Tab)} />
        )}
      </motion.div>
    </div>
  );
}
